import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {OntologyIndex, OntologyTree, OntologyTreeNode} from '../ontology/ontologyTree'
import {collectTermUris, FileTags} from '../ontology/tags'
import {getLogger} from '../logUtils'

/** A resolved ontology term's tree data plus the URI it was looked up by (for debug/verbose display). */
export interface OntologyInfo extends OntologyTreeNode {
  uri: string
}

/**
 * CDN location of the ontology tree. Icons live next to it under `icons/`
 * (e.g. `.../ontology/latest/icons/bam.svg`), matching how the publish workflow
 * lays out the bundle.
 */
const TREE_URL = process.env.REACT_APP_ONTOLOGY_TREE_URL
const ICON_BASE_URL = TREE_URL ? TREE_URL.replace(/\/[^/]+$/, '/icons') : undefined

async function fetchOntologyTree(): Promise<OntologyTree> {
  if (!TREE_URL) {
    throw new Error('REACT_APP_ONTOLOGY_TREE_URL is not configured')
  }
  const response = await fetch(TREE_URL)
  if (!response.ok) {
    throw new Error(`Failed to fetch ontology tree (${response.status})`)
  }
  return response.json()
}

/**
 * What the file manager needs from the ontology tree: given a file's `tags`,
 * whether it is openable in a web viewer and which icon to show. Both degrade to
 * `undefined` ("unknown") when the tree is unavailable or the file carries no
 * recognizable term, so callers keep today's always-attempt behavior — never a
 * false "unsupported".
 */
export interface OntologyResolver {
  ready: boolean
  /** undefined ⇒ unknown (no/unrecognized tags): treat as openable, no regression. */
  isOpenable: (tags: FileTags | undefined | null) => boolean | undefined
  /** Full CDN URL of the per-format icon, or undefined to fall back to the default icon. */
  iconUrl: (tags: FileTags | undefined | null) => string | undefined
  /** Label + (if published) description of the file's primary known ontology term, for tooltips. */
  ontologyInfo: (tags: FileTags | undefined | null) => OntologyInfo | undefined
}

/**
 * Fetches + caches `ontology-tree.json` and exposes a memoized {@link OntologyResolver}.
 * The tree is near-static and already CloudFront-cached, so it is held with a
 * long stale/cache time and fetched once per session.
 */
export const useOntologyTree = () => {
  const query = useQuery<OntologyTree, Error>(['ontologyTree', TREE_URL], fetchOntologyTree, {
    enabled: !!TREE_URL,
    staleTime: 1000 * 60 * 60 * 6, // 6h; CloudFront already caches the file
    cacheTime: 1000 * 60 * 60 * 24,
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    onError: (error) => {
      getLogger().warn('Ontology tree unavailable; file openability/icons fall back to defaults:', error)
    },
  })

  const index = useMemo(() => new OntologyIndex(query.data), [query.data])

  const resolver = useMemo<OntologyResolver>(() => {
    const ready = !!query.data
    return {
      ready,
      isOpenable: (tags) => {
        if (!ready) {
          return undefined
        }
        const known = collectTermUris(tags).filter((uri) => index.has(uri))
        if (known.length === 0) {
          return undefined
        }
        return known.some((uri) => index.isViewerSupported(uri))
      },
      iconUrl: (tags) => {
        if (!ready || !ICON_BASE_URL) {
          return undefined
        }
        for (const uri of collectTermUris(tags)) {
          const icon = index.effectiveIcon(uri)
          if (icon) {
            return `${ICON_BASE_URL}/${icon}`
          }
        }
        return undefined
      },
      ontologyInfo: (tags) => {
        if (!ready) {
          return undefined
        }
        const preferred = index.preferredTerm(collectTermUris(tags))
        return preferred ? {...preferred.node, uri: preferred.uri} : undefined
      },
    }
  }, [index, query.data])

  return {...query, index, resolver, iconBaseUrl: ICON_BASE_URL}
}
